import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { customersService, Customer } from '../services/customersService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import { Users, Save, X, AlertTriangle } from 'lucide-react';

interface CustomerFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (customer: Customer) => void;
  customer?: Customer | null;
}

export const ModernCustomerForm: React.FC<CustomerFormProps> = ({
  open,
  onClose,
  onSave,
  customer,
}) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    stateAbbr: '',
    zipcode: '',
    birthday: '',
  });

  useEffect(() => {
    if (open) {
      if (customer) {
        const customerAny = customer as any;
        // Handle Zalo customers that might have 'name' instead of firstName/lastName
        let firstName = customer.firstName || '';
        let lastName = customer.lastName || '';

        if (!firstName && !lastName && customerAny.name) {
          const nameParts = customerAny.name.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }

        setFormData({
          firstName,
          lastName,
          email: customer.email || '',
          phone: customerAny.phone || '',
          address: customer.address || '',
          city: customer.city || '',
          stateAbbr: customer.stateAbbr || '',
          zipcode: customer.zipcode || '',
          birthday: customer.birthday
            ? new Date(customer.birthday).toISOString().split('T')[0]
            : '',
        });
      } else {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          stateAbbr: '',
          zipcode: '',
          birthday: '',
        });
      }
      setError(null);
    }
  }, [open, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const customerData: Partial<Customer> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        address: formData.address || undefined,
        city: formData.city || undefined,
        stateAbbr: formData.stateAbbr || undefined,
        zipcode: formData.zipcode || undefined,
        birthday: formData.birthday ? new Date(formData.birthday) : undefined,
      };

      // Add phone if provided (it might not be in the Customer interface)
      if (formData.phone) {
        (customerData as any).phone = formData.phone;
      }

      let savedCustomer: Customer;
      if (customer?.id) {
        savedCustomer = await customersService.updateCustomer(
          customer.id,
          customerData
        );
      } else {
        savedCustomer = await customersService.createCustomer(customerData);
      }

      onSave(savedCustomer);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : translate('pos.dashboard.error_saving', 'Failed to save customer');
      setError(errorMessage);
      console.error('Error saving customer:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>
              {customer
                ? translate('pos.common.action.edit', 'Edit Customer')
                : translate('pos.common.action.create', 'Create Customer')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {customer
              ? translate(
                  'pos.dashboard.edit_customer_description',
                  'Update customer information'
                )
              : translate(
                  'pos.dashboard.create_customer_description',
                  'Add a new customer to the system'
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  {translate(
                    'pos.customers.fields.firstName',
                    'First Name'
                  )}{' '}
                  *
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  data-sensitive-field="firstName"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">
                  {translate(
                    'pos.customers.fields.lastName',
                    'Last Name'
                  )}{' '}
                  *
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  data-sensitive-field="lastName"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">
                  {translate('pos.customers.fields.email', 'Email')} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  data-sensitive-field="email"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">
                  {translate('pos.customers.fields.phone', 'Phone')}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  data-sensitive-field="phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">
                {translate('pos.customers.fields.address', 'Address')}
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                data-sensitive-field="address"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">
                  {translate('pos.customers.fields.city', 'City')}
                </Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stateAbbr">
                  {translate(
                    'pos.customers.fields.stateAbbr',
                    'State'
                  )}
                </Label>
                <Input
                  id="stateAbbr"
                  value={formData.stateAbbr}
                  onChange={(e) =>
                    setFormData({ ...formData, stateAbbr: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="zipcode">
                  {translate(
                    'pos.customers.fields.zipcode',
                    'Zipcode'
                  )}
                </Label>
                <Input
                  id="zipcode"
                  value={formData.zipcode}
                  onChange={(e) =>
                    setFormData({ ...formData, zipcode: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthday">
                {translate(
                  'pos.customers.fields.birthday',
                  'Birthday'
                )}
              </Label>
              <Input
                id="birthday"
                type="date"
                value={formData.birthday}
                onChange={(e) =>
                  setFormData({ ...formData, birthday: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading
                ? translate('pos.common.action.saving', 'Saving...')
                : translate('pos.common.action.save', 'Save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
